import { bool, instanceOf, node, arrayOf, string } from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Modal, Select } from "semantic-ui-react";
import { get } from "lodash";
import "./configurator.scss";
import OptionsMenu from "shared/components/roomBook/OptionsMenu";
import ConfiguratorActions from "buyer_portal/actions/configuratorActions";
import {
  getResource,
  SUB_LINE_ITEM_RESOURCE
} from "shared/actions/actionLocator";
import { stopPropagation } from "shared/helpers/event";
import ConfiguratorImage from "shared/components/configurator/ConfiguratorImage";
import { createStructuredSelector } from "reselect";
import {
  getActiveConfiguratorViews,
  getVisibleConfiguratorLineItems,
  getConfiguratorSubLineItems,
  getConfiguratorViews,
  getConfiguratorTitle
} from "shared/selectors";
import Button from "semantic-ui-react/dist/es/elements/Button/Button";
import { FormattedMessage } from "react-intl";
import {
  RoomBookController,
  roomBookController
} from "shared/actions/roomBookController";
import { toggleConfirmationDialog } from "shared/components/dialogs/ConfirmationDialogContainer";
import { ConfiguratorSubLineItemShape } from "shared/shapes/configuratorSubLineItem.shape";
import { configuratorViewShape } from "shared/shapes/configuratorView.shape";
import { ConfiguratorLineItemShape } from "shared/shapes/configuratorLineItem.shape";
import RemoteImage from "./RemoteImage";
import { withRouter } from "../../helpers/withRouter";

const viewHasCategories = (currentView, productImageCategories) => {
  const viewCategories = get(currentView, "productCategories", []);
  return [...productImageCategories].every(cat => viewCategories.includes(cat));
};

const extractImageCategories = subLineItems => {
  const imageCategories = new Set();
  subLineItems.forEach(subLineItem => {
    subLineItem.options.forEach(({ category }) => {
      if (category) imageCategories.add(category);
    });
  });
  return imageCategories;
};

class Configurator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      open: false,
      selection: {},
      selectedSubLineItemId: undefined,
      selectedLineItemId: undefined,
      currentView: undefined,
      viewSelectorOpen: false
    };
  }

  handleOpen = () => {
    const { subLineItems, lineItems } = this.props;
    const selectedSubLineItemId =
      subLineItems.length === 1 ? subLineItems[0].id : null;
    const selectedLineItemId = lineItems.length === 1 ? lineItems[0].id : null;
    this.setState({
      open: true,
      selectedSubLineItemId,
      selectedLineItemId,
      currentView: this.selectView(selectedSubLineItemId, selectedLineItemId)
    });
  };

  handleViewSelectorOpen = () => {
    this.setState({ viewSelectorOpen: true });
  };

  handleViewSelectorClose = () => {
    this.setState({ viewSelectorOpen: false });
  };

  handleChangeView = (_, { value }) => {
    const { views } = this.props;
    this.setState({ currentView: views.find(({ room }) => room === value) });
  };

  handleSelectSubLineItemId = selectedSubLineItemId => {
    this.setState(({ selectedLineItemId }, { lineItems }) => {
      const selectedLineItem = lineItems.find(
        li => li.id === selectedLineItemId
      );
      const newSelectedLineItemId =
        selectedLineItem?.subLineItemIds.length === 1
          ? null
          : selectedLineItemId;
      return {
        selectedSubLineItemId,
        selectedLineItemId: newSelectedLineItemId,
        currentView: this.selectView(
          selectedSubLineItemId,
          newSelectedLineItemId
        )
      };
    });
  };

  handleSelectLineItemId = selectedLineItemId => {
    const { lineItems } = this.props;
    const selectedLineItem = lineItems.find(li => li.id === selectedLineItemId);
    const selectedSubLineItemId =
      selectedLineItem?.subLineItemIds.length === 1
        ? selectedLineItem.subLineItemIds[0]
        : null;

    this.setState({
      selectedLineItemId,
      selectedSubLineItemId,
      currentView: this.selectView(selectedSubLineItemId, selectedLineItemId)
    });
  };

  handleSelectOption = option => {
    const { selection } = this.state;
    const subLineItem = this.getSubLineItemById(option.subLineItemId);

    if (!subLineItem.status.selectable) {
      return;
    }

    const key = subLineItem.id.toString();
    if (
      subLineItem.hasBuyerSelection &&
      subLineItem.selectedOption.id === option.id
    ) {
      const { [key]: _, ...newSelection } = selection;
      this.setState({ selection: newSelection });
    } else {
      this.setState({ selection: { ...selection, [key]: option } });
    }
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  getSubLineItemById = subLineItemId => {
    const { subLineItems } = this.props;
    return subLineItems.find(sli => sli.id === subLineItemId);
  };

  selectView(subLineItemId, lineItemId) {
    const { views, activeViews, subLineItems } = this.props;
    const { currentView } = this.state || views[0] || {};

    const relevantSubLineItems = subLineItems.filter(subLineItem =>
      lineItemId ? subLineItem.lineItemId === lineItemId : subLineItem.isActive
    );
    const subLineItem = subLineItemId && this.getSubLineItemById(subLineItemId);

    const lineItemProductImageCategories = extractImageCategories(
      relevantSubLineItems
    );
    const subLineItemProductImageCategories = subLineItem
      ? extractImageCategories([subLineItem])
      : lineItemProductImageCategories;

    if (!viewHasCategories(currentView, subLineItemProductImageCategories)) {
      const newView =
        views.find(view =>
          viewHasCategories(view, subLineItemProductImageCategories)
        ) ||
        views.find(view =>
          viewHasCategories(view, lineItemProductImageCategories)
        );
      if (newView) {
        return newView;
      }
    }
    const rooms = activeViews.map(view => view?.room);
    return rooms.includes(currentView?.room) ||
      !viewHasCategories(activeViews[0], lineItemProductImageCategories)
      ? currentView
      : activeViews[0];
  }

  renderViewSelector() {
    const { activeViews } = this.props;
    const { currentView, selectedSubLineItemId, viewSelectorOpen } = this.state;
    if (activeViews.length < 2) {
      return null;
    }
    const productImageCategories =
      selectedSubLineItemId &&
      extractImageCategories([this.getSubLineItemById(selectedSubLineItemId)]);

    const viewOptions = activeViews.map(view => ({
      text: view.title,
      value: view.room,
      disabled:
        selectedSubLineItemId &&
        !viewHasCategories(view, productImageCategories)
    }));
    return (
      <Select
        color="black"
        placeholder="Ansicht"
        options={viewOptions}
        data-component="viewSelector"
        value={get(currentView, "room")}
        onTouchStart={stopPropagation}
        onTouchEnd={stopPropagation}
        onChange={this.handleChangeView}
        onOpen={this.handleViewSelectorOpen}
        onClose={this.handleViewSelectorClose}
        open={viewSelectorOpen}
      />
    );
  }

  renderContent() {
    const {
      lineItems,
      subLineItems,
      configuratorId,
      title: roomName,
      ctrl,
      confirmationDialog,
      views
    } = this.props;
    const {
      selection,
      currentView,
      selectedSubLineItemId,
      selectedLineItemId
    } = this.state;

    const rooms = views.map(view => view?.room);
    const selectedView = rooms.includes(currentView?.room)
      ? currentView
      : views[0];

    const selectedSubLineItem = subLineItems.find(
      sli => sli.id === selectedSubLineItemId
    );
    const selectedLineItem = lineItems.find(li => li.id === selectedLineItemId);

    return (
      <div className="configurator-wrapper">
        <ConfiguratorImage
          configuratorId={configuratorId}
          roomId={configuratorId}
          selection={selection}
          view={selectedView}
          selectedSubLineItem={selectedSubLineItem}
        >
          {({ imageURL, prefetchImageURLs }) => (
            <RemoteImage
              imageURL={imageURL}
              prefetchImageURLs={prefetchImageURLs}
            >
              <div className="logo" />
              {/* TODO: Put back when we have "real" multiple views, but with a camera icon instead */}
              {/* currently moved to OptionsMenu below for sales presentation */}
              {/* {this.renderViewSelector()} */}
            </RemoteImage>
          )}
        </ConfiguratorImage>
        <OptionsMenu
          onSelectOption={this.handleSelectOption}
          onSelectLineItemId={this.handleSelectLineItemId}
          onSelectSubLineItemId={this.handleSelectSubLineItemId}
          lineItems={lineItems}
          subLineItems={subLineItems}
          selection={selection}
          roomName={roomName}
          selectedSubLineItem={selectedSubLineItem}
          selectedLineItem={selectedLineItem}
          ctrl={ctrl}
          confirmationDialog={confirmationDialog}
        >
          {this.renderViewSelector()}
        </OptionsMenu>
      </div>
    );
  }

  render() {
    const { views, trigger, only3D, lineItems } = this.props;
    const { open, loading } = this.state;

    if (views.length < 1 || lineItems.length < 1) {
      return null;
    }

    if (only3D)
      return (
        <div
          data-component="configurator"
          data-portal={window.initialProps.portal}
        >
          <div className="content">{this.renderContent()}</div>
        </div>
      );

    return (
      <Modal
        size="fullscreen"
        basic
        data-component="configurator"
        data-portal={window.initialProps.portal}
        onClose={this.handleClose}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        onOpen={this.handleOpen}
        open={open}
        trigger={trigger}
      >
        <Modal.Content>{this.renderContent()}</Modal.Content>
        <Modal.Actions>
          <div className="disclaimer">
            {/* Do not delete the inner span, we may hide and replace it with Project-Related-CSS */}
            <span data-id="default Disclaimer">
              <FormattedMessage id="configurator.disclaimer" />
            </span>
          </div>
          <Button
            positive
            content="Übernehmen und Schließen"
            onClick={this.handleClose}
            key="abort"
            loading={loading}
            disabled={loading}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

Configurator.propTypes = {
  configuratorId: string.isRequired,
  subLineItems: arrayOf(ConfiguratorSubLineItemShape),
  lineItems: arrayOf(ConfiguratorLineItemShape),
  title: string.isRequired,
  trigger: node,
  views: arrayOf(configuratorViewShape).isRequired,
  activeViews: arrayOf(configuratorViewShape).isRequired,
  only3D: bool,
  ctrl: instanceOf(RoomBookController).isRequired
};

Configurator.defaultProps = {
  trigger: null,
  only3D: false,
  subLineItems: [],
  lineItems: []
};

const mapDispatchToProps = (dispatch, props) => {
  const { id } = props.params;
  const LineItemResource = getResource(SUB_LINE_ITEM_RESOURCE);

  return {
    lineItemResource: new LineItemResource(dispatch, id),
    configuratorActions: new ConfiguratorActions(dispatch),
    ctrl: roomBookController(dispatch, id),
    confirmationDialog: toggleConfirmationDialog(dispatch)
  };
};

const mapStateToProps = createStructuredSelector({
  subLineItems: getConfiguratorSubLineItems,
  lineItems: getVisibleConfiguratorLineItems,
  title: getConfiguratorTitle,
  views: getConfiguratorViews,
  activeViews: getActiveConfiguratorViews
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Configurator)
);
